<script lang="ts" setup>
import { PrimaryButton, CollapsibleContent, ModalDialog } from '@hfc/ui-core';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useUrlHelper } from '@/page/infrastructure/composition/useUrlHelper';
import { StaffMember } from '@/shared/domain/StaffMember';
import StaffMemberComp from '@/shared/infrastructure/components/staffMember/StaffMember.vue';

const props = withDefaults(defineProps<{
  person: StaffMember;
  isExpanded?: boolean;
  buttonLabel?: string;
  buttonLabelIcon?: string;
  enableModal?: boolean;
  lightTheme?: boolean;
  compactMode?: boolean;
}>(), {
  isExpanded: false,
  buttonLabel: 'Read Bio',
  buttonLabelIcon: 'plus',
  enableModal: false,
  lightTheme: false,
  compactMode: false,
});

const { getCdnUrl } = useUrlHelper();
const isExpanded = ref(props.isExpanded);
const showModal = ref(false);
const isMobile = ref(false);
const collapsibleContentRef = ref<{ reset: () => void }>();

const handleClick = (): void => {
  if (props.enableModal && !isMobile.value) {
    showModal.value = true;
  } else {
    isExpanded.value = !isExpanded.value;
  }
};

const reset = (): void => {
  isExpanded.value = false;
  showModal.value = false;
  collapsibleContentRef.value?.reset();
};

const handleResize = (): void => {
  isMobile.value = window.innerWidth < 768;
  reset();
};

onMounted(() => {
  window.addEventListener('resize', handleResize);

  handleResize();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});

</script>

<template>
  <ModalDialog v-if="showModal" @close="showModal = false" size="lg" :show-footer="false" :show-header="false" content-class="p-0 relative space-y-0" modal-class="rounded-3xl" overlay-class="bg-blue-800/50">
    <template #content>
      <div class="absolute top-3 right-3 z-10 flex items-center justify-center cursor-pointer">
        <hfc-icon icon="times" class="w-6 h-6 text-black/50 hover:text-black/75" @click="showModal = false" />
      </div>
      <StaffMemberComp :person="person" :enable-modal="false" light-theme />
    </template>
  </ModalDialog>
  <div class="relative w-full" :class="compactMode ? '' : '@container'">
    <div class="flex flex-col w-full">
      <img :src="getCdnUrl(person.avatar)" :alt="person.name" class="w-[150px] h-[180px] @lg:w-[183px] @lg:h-[220px] inline-block mx-auto relative z-[1] @lg:absolute @lg:top-6 @lg:left-6 object-cover rounded-3xl @lg:rounded-lg" />
      <div class="bg-blue-200 pb-6 @lg:pb-0 @lg:overflow-hidden rounded-3xl" :class="lightTheme && !enableModal ? 'bg-white' : 'bg-blue-200'">
        <div class="flex flex-col gap-1 bg-blue-200 -mt-[110px] pt-[130px] @lg:pl-[230px] @lg:pt-5 @lg:mt-0 w-full text-center @lg:text-left rounded-2xl @lg:rounded-none" :class="lightTheme ? '' : '@lg:bg-blue-500 @lg:text-white'">
          <div class="font-semibold uppercase text-blue-700 text-lg @lg:text-xl" :class="lightTheme ? '' : '@lg:text-white'">{{ person.name }}</div>
          <div class="text-sm pb-4 min-h-[60px] @lg:min-h-fit text-md @lg:text-lg px-5">{{ person.role }}</div>
        </div>
        <CollapsibleContent ref="collapsibleContentRef" :is-open="isExpanded" wrapper-class="border-0 bg-transparent rounded-none @lg:min-h-[174px]" collapsible-content-class="@lg:grid-rows-[1fr]" :reversed="true" :closeable="!enableModal || isMobile">
          <template #title>
            <PrimaryButton class="@lg:hidden w-auto mx-auto absolute -bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1" @click="handleClick">
              {{ buttonLabel }} <hfc-icon :icon="['fas', buttonLabelIcon]" />
            </PrimaryButton>
          </template>
          <template #content>
            <div class="text-black sm:block p-4 @lg:pl-[230px] text-sm" :class="lightTheme && !enableModal ? 'bg-white' : 'bg-blue-200'" v-html="person.bio" />
          </template>
        </CollapsibleContent>
      </div>
    </div>
  </div>
</template>
